import { Shader } from './Shader';
import { BlendResource } from './BlendResource';
import { BlendState } from './BlendState';
import { IShader } from './IShader';
import { Effect } from './Effect';
import { EmptyShader } from './EmptyShader';
import { IResourceCache } from './IResourceCache';
import { RenderDevice } from './RenderDevice';
import { LazyShaderCache } from '../shaders/LazyShaderCache';
import { Blend } from './Blend';


export class RenderSupport {
  private _resourceCache: IResourceCache;
  private _renderDevice: RenderDevice;
  private _shaderCache: LazyShaderCache;

  private _cachedId: number = -1;
  private _cached: Shader | null = null;

  private _alpha: BlendResource | null = null;
  private _oneInverseSourceAlpha: BlendResource | null = null;

  private _shaders: Map<number, Shader> = new Map<number, Shader>();

  get alphaBlend(): BlendState {
    if (!this._alpha) {
      this._alpha = this._resourceCache.getResource(
        BlendResource.TypeId,
        'SourceAlpha_InverseSourceAlpha'
      );
      
      if (!this._alpha) {
        this._alpha = new BlendResource('SourceAlpha_InverseSourceAlpha');
        const blendState = this._renderDevice.createBlend(true, Blend.SrcAlpha, Blend.InvSrcAlpha);
        if (blendState) {
          this._alpha.construct(blendState);
        } else {
          return new BlendState(true, Blend.SrcAlpha, Blend.InvSrcAlpha);
        }
      }
    }
    return this._alpha!.data!;
  }

  get oneInverseSourceAlpha(): BlendState {
    if (!this._oneInverseSourceAlpha) {
      this._oneInverseSourceAlpha = this._resourceCache.getResource(
        BlendResource.TypeId,
        'One_InverseSourceAlpha'
      );
      
      if (!this._oneInverseSourceAlpha) {
        this._oneInverseSourceAlpha = new BlendResource('One_InverseSourceAlpha');
        const blendState = this._renderDevice.createBlend(true, Blend.One, Blend.InvSrcAlpha);
        if (blendState) {
          this._oneInverseSourceAlpha.construct(blendState);
        } else {
          return new BlendState(true, Blend.One, Blend.InvSrcAlpha);
        }
      }
    }
    return this._oneInverseSourceAlpha!.data!;
  }

  constructor(resourceCache: IResourceCache, renderDevice: RenderDevice) {
    this._resourceCache = resourceCache;
    this._renderDevice = renderDevice;
    this._shaderCache = new LazyShaderCache(renderDevice);
  }

  resolve(effectId: number): IShader {
    if (this._cachedId === effectId) {
      return this._cached!;
    }

    this._cachedId = effectId;
    this._cached = this._shaders.get(effectId) ?? null;

    if (this._cached) {
      return this._cached;
    }

    try {
      const program = this._shaderCache.getProgram(effectId);

      if (!program) {
        console.warn(`RenderSupport: Failed to get shader program for effectId ${effectId}`);
        return new EmptyShader();
      }

      this._cached = new Shader(program);
      this._shaders.set(effectId, this._cached);

    } catch (e) {
      console.error(`RenderSupport: Failed to resolve shader for effectId ${effectId}:`, e);
      return new EmptyShader();
    }

    return this._cached;
  }

  contextLost(): void {
    this._shaders.forEach((s, _) => {
      s.dispose();
    });
    this._shaders.clear();
    this._cachedId = -1;
    this._cached = null;

    this._shaderCache.clearCache();
  }

  contextReady(): void {}

  dispose(): void {
    this.contextLost();
    this._shaderCache.dispose();
  }
}
